import React from "react";

const NotificationContext = React.createContext(null);

export function NotificationProvider({ children }) {
    const [notification, setNotification] = React.useState(null);
    const [isHovered, setIsHovered] = React.useState(false);
    const timeoutRef = React.useRef(null);
    const remainingMsRef = React.useRef(0);
    const startedAtRef = React.useRef(0);

    const clearTimer = React.useCallback(() => {
        if (timeoutRef.current) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const closeNotification = React.useCallback(() => {
        clearTimer();
        setNotification(null);
        remainingMsRef.current = 0;
        startedAtRef.current = 0;
    }, [clearTimer]);

    const startTimer = React.useCallback((durationMs) => {
        clearTimer();
        const duration = Math.max(0, Number(durationMs) || 0);
        remainingMsRef.current = duration;
        startedAtRef.current = Date.now();
        if (duration > 0) {
            timeoutRef.current = window.setTimeout(() => {
                closeNotification();
            }, duration);
        }
    }, [clearTimer, closeNotification]);

    const showNotification = React.useCallback((payload) => {
        const text = String(payload?.text || "").trim();
        if (!text) return;
        const type = payload?.type === "error" ? "error" : "success";
        const durationMs = Number(payload?.durationMs) > 0 ? Number(payload.durationMs) : 5000;
        setNotification({ text, type });
        setIsHovered(false);
        startTimer(durationMs);
    }, [startTimer]);

    React.useEffect(() => {
        if (!notification) return undefined;
        if (isHovered) {
            if (startedAtRef.current > 0) {
                const elapsed = Date.now() - startedAtRef.current;
                remainingMsRef.current = Math.max(0, remainingMsRef.current - elapsed);
            }
            clearTimer();
            startedAtRef.current = 0;
            return undefined;
        }
        if (!timeoutRef.current && remainingMsRef.current > 0) {
            startedAtRef.current = Date.now();
            timeoutRef.current = window.setTimeout(() => {
                closeNotification();
            }, remainingMsRef.current);
        }
        return undefined;
    }, [notification, isHovered, clearTimer, closeNotification]);

    React.useEffect(() => () => clearTimer(), [clearTimer]);

    return (
        <NotificationContext.Provider value={{ showNotification, closeNotification }}>
            {children}
            {notification && (
                <div className="app-toast-wrap" role="status" aria-live="polite">
                    <div
                        className={`app-toast app-toast--${notification.type}`}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                    >
                        <button type="button" className="app-toast__close" onClick={closeNotification} aria-label="Закрыть уведомление">
                            ×
                        </button>
                        <div className="app-toast__text">{notification.text}</div>
                    </div>
                </div>
            )}
        </NotificationContext.Provider>
    );
}

export function useNotification() {
    const ctx = React.useContext(NotificationContext);
    if (!ctx) {
        throw new Error("useNotification must be used within NotificationProvider");
    }
    return ctx;
}
